import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { fetchRouteReport } from './api'
import { PageTitle } from '../../../_metronic/layout/core'
import CRUDTable from '../../../components/table/CRUDTable'
import toast, { Toaster } from 'react-hot-toast'
import viewProps from '../viewProps'
import { Error500 } from '../../modules/errors/components/Error500'
import TopBarProgress from 'react-topbar-progress-indicator'
import { Processing } from '../../../components/Loaders'
import { Helmet } from 'react-helmet'
import { RouteReportPayload } from './type'
import ExportToExcelButton from './ExportToExcelButton' // ensure this points to actual file

// normalizeNewlines: convert literal "\n" (backslash+n) to real newline and normalize CRLF
const normalizeNewlines = (input?: any): string => {
  if (input === null || input === undefined) return ''
  const s = String(input)
  return s
    .replace(/\\r\\n/g, '\n') // literal backslash r + backslash n
    .replace(/\\n/g, '\n')    // literal backslash n
    .replace(/\r\n/g, '\n')   // CRLF
}

const RouteReport = () => {
  const [filters, setFilters] = useState<RouteReportPayload>({ date: '' })

  const {
    data: reportData,
    isLoading: isLoadingReport,
    isFetching,
    isError,
    refetch
  } = useQuery(
    'route-report',
    () => fetchRouteReport({ date: filters.date }),
    {
      enabled: false,
      refetchOnWindowFocus: false
    }
  )

  const handleApplyFilters = () => {
    const { date } = filters
    if (!date) {
      toast.error('Please select a date.')
      return
    }
    toast.loading('Applying filters...')
    refetch()
      .then(() => {
        toast.dismiss()
        toast.success('Filters applied successfully.')
      })
      .catch(() => {
        toast.dismiss()
        toast.error('Failed to fetch Route report.')
      })
  }

  // 1) build exportData (plain strings, normalized newlines) — use this for Excel
  const exportData = (reportData?.data || []).map((row: any) => {
    return {
      ...row,
      zone_street_buildings: normalizeNewlines(row.zone_street_buildings),
      boys_girls: 'Boys & Girls'
    }
  })

  // 2) build displayData (JSX node for datatable). Use exportData as base (so same normalized string).
  const displayData = exportData.map((row: any) => ({
    ...row,
    zone_street_buildings: (
      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
        {row.zone_street_buildings}
      </div>
    ) as any, // cast to any if types strict
    boys_girls: 'Boys & Girls',
  }))

  return (
    <>
      <Helmet>
        <title>TrackNAB | Route Chart Report</title>
      </Helmet>
      <PageTitle>Route Chart Report</PageTitle>

      {/* Filters */}
      <div className="mb-4 p-4 border rounded shadow-sm bg-light">
        <div className="row g-3">
          <div className="col-md-2">
            <label className="form-label">Select Date</label>
            <input
              type="date"
              className="form-control"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
            />
          </div>

          <div className="col-md-12 d-flex justify-content-end">
            <button className="btn btn-primary mt-2" onClick={handleApplyFilters}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoadingReport && <Processing />}
      {isError && <Error500 />}

      {reportData?.data?.length! > 0 ? (
        <CRUDTable
          data={displayData} // show JSX nodes here
          {...viewProps.RouteReport}
          fieldToLabelMap={viewProps.RouteReport.primaryFieldsToLabelMap}          
          headerExtras={
            <>
              <div className="ms-auto">
                <ExportToExcelButton
                  data={exportData} // IMPORTANT: pass plain-string exportData
                  fileName="Route Chart Report"
                  heading={`Mes School Route Chart Report - ${filters.date}`}
                  titleMap={viewProps.RouteReport.primaryFieldsToLabelMap}                  
                />
              </div>
            </>
          }
        />
      ) : (
        <div className="fs-2 p-6 text-center">Apply filters / No data for applied filters</div>
      )}

      {isFetching && <TopBarProgress />}
      <Toaster />
    </>
  )
}

export default RouteReport
